import { z } from "zod";
import { slugify } from "./slug.js";

export function resolveEditorialCategory<T extends { slug: string; name: string; description?: string | null }>(
  value: string, categories: T[],
): T | { slug: string; name: string; description: string } {
  const slug = slugify(value);
  return categories.find(category => category.slug === value || slugify(category.name) === slug || category.slug === slug)
    ?? { slug, name: value, description: `${value} insights from Premier Pharma.` };
}

/** The scheduled instant is authoritative, including legacy rows without publishedAt. */
export function editorialPublicationDate<T extends Date | string>(article: {
  publishedAt?: T | null; scheduledAt?: T | null; status?: string;
}): T | null {
  return article.publishedAt ?? (article.status === "scheduled" ? article.scheduledAt ?? null : null);
}

export const editorialStatus = ["draft", "review", "scheduled", "published", "archived"] as const;
export const editorialContentTypes = ["guide", "article", "analysis", "company_news", "press_mention"] as const;

/** Shared policy content for public API and server-rendered HTML. */
export const EDITORIAL_POLICY_DATA = {
  title: "Editorial policy",
  updatedAt: "2026-01-01",
  sections: [{ heading: "Standards", body: "Our editorial content is educational, sourced, reviewed, and not medical or legal advice." }],
};

function stripEditorialHtml(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(?:p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Convert plain text or common block-editor JSON into public, readable text. */
export function editorialContentToText(content: unknown): string {
  if (typeof content === "string") return stripEditorialHtml(content);
  if (Array.isArray(content)) {
    return content.map(editorialContentToText).filter(Boolean).join("\n\n");
  }
  if (!content || typeof content !== "object") return "";

  const record = content as Record<string, unknown>;
  if (record.type === "header" || record.type === "heading") {
    const data = record.data && typeof record.data === "object" ? record.data as Record<string, unknown> : record;
    const attrs = record.attrs && typeof record.attrs === "object" ? record.attrs as Record<string, unknown> : {};
    const level = Number(data.level ?? attrs.level ?? 2);
    // The article title is the only H1. Preserve section levels H2–H6.
    const headingLevel = Number.isInteger(level) ? Math.min(6, Math.max(2, level)) : 2;
    const text = editorialContentToText(data.text ?? record.content).replace(/\s*\n\s*/g, " ");
    return text ? `${"#".repeat(headingLevel)} ${text}` : "";
  }
  if (Array.isArray(record.blocks)) {
    return record.blocks.map(editorialContentToText).filter(Boolean).join("\n\n");
  }
  for (const key of ["text", "body", "value", "content", "data"]) {
    const text = editorialContentToText(record[key]);
    if (text) return text;
  }
  return "";
}

export function hasEditorialContent(content: unknown): boolean {
  return editorialContentToText(content).trim().length > 0;
}

/** Preserve plain text, while turning intentionally entered JSON back into structured content. */
export function parseEditorialContentInput(content: unknown): unknown {
  if (typeof content !== "string") return content;
  const trimmed = content.trim();
  if (!trimmed || (!trimmed.startsWith("{") && !trimmed.startsWith("["))) return content;
  try {
    return JSON.parse(trimmed);
  } catch {
    return content;
  }
}

export function formatEditorialContentForEditor(content: unknown): string {
  return typeof content === "string" ? content : JSON.stringify(content ?? { blocks: [] }, null, 2);
}

export const editorialArticleInput = z.object({
  slug: z.string().trim().min(1).max(160).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  title: z.string().trim().min(1).max(300),
  excerpt: z.string().trim().min(1).max(1000),
  content: z.unknown(),
  contentType: z.enum(editorialContentTypes),
  category: z.string().trim().min(1).max(60),
  authorId: z.number().int().positive(),
  reviewerId: z.number().int().positive().nullable().optional(),
  sources: z.array(z.string().url()).default([]),
  relatedPages: z.array(z.string().startsWith("/")).default([]),
  primaryAudience: z.string().trim().min(1).max(200),
  searchIntent: z.string().trim().min(1).max(200),
  primaryConversionGoal: z.string().trim().min(1).max(100),
  canonicalUrl: z.string().url(),
  primaryKeyword: z.string().trim().min(1).max(200),
  secondaryKeywords: z.array(z.string().trim().min(1)).default([]),
  entities: z.array(z.string().trim().min(1)).default([]),
  audience: z.string().trim().max(200).optional(),
  topicCluster: z.string().trim().max(100).optional(),
  canonicalTopic: z.string().trim().min(1).max(200),
  primaryQuestion: z.string().trim().max(1000).optional(),
  internalLinks: z.array(z.string().startsWith("/")).default([]),
  stockedProducts: z.array(z.number().int().positive()).max(500).default([]),
  nextReviewAt: z.coerce.date().nullable().optional(),
  scheduledAt: z.coerce.date().nullable().optional(),
});

export function validatePublishableArticle(article: {
  authorId?: number | null;
  reviewerId?: number | null;
  sources?: unknown;
  publishedAt?: Date | null;
  canonicalUrl?: string | null;
  title?: string | null;
  content?: unknown;
  scheduledAt?: Date | null;
  primaryKeyword?: string | null;
  canonicalTopic?: string | null;
  socialImageValid?: boolean;
}): string[] {
  const errors: string[] = [];
  if (!article.authorId) errors.push("A named author is required");
  if (!article.reviewerId) errors.push("An expert reviewer is required");
  if (!Array.isArray(article.sources) || article.sources.length === 0) errors.push("At least one source is required");
  if (!article.canonicalUrl) errors.push("A canonical URL is required");
  if (!article.title?.trim()) errors.push("A title is required");
  if (!hasEditorialContent(article.content)) errors.push("Article content is required");
  if (!article.primaryKeyword?.trim()) errors.push("A primary keyword is required");
  if (!article.canonicalTopic?.trim()) errors.push("A canonical topic is required");
  if (article.socialImageValid === false) errors.push("The selected social image is missing or does not meet the 1200 × 630 minimum");
  return errors;
}

export function validateEditorialTransitionRequirements(
  article: Parameters<typeof validatePublishableArticle>[0],
  target: "scheduled" | "published",
): string[] {
  const errors = validatePublishableArticle(article);
  if (target === "scheduled" && !article.scheduledAt) {
    errors.unshift("A scheduled publication date is required");
  }
  return errors;
}

/** Owner-approved publishing default; an explicitly selected reviewer is retained. */
export function editorialPublicationDefaults<T extends { authorId?: number | null; reviewerId?: number | null }>(article: T) {
  return { ...article, reviewerId: article.reviewerId ?? article.authorId ?? null };
}

export const approvedEditorialClusters = ["dscsa-compliance", "pharmacy-operations", "drug-supply", "company-news"] as const;
export type KeywordInventoryRow = { keyword: string; ownerTitle?: string; ownerPath?: string; status: string; cluster?: string; overlap: boolean; gap: boolean };
export function buildKeywordInventory(items: Array<{ title: string; slug: string; status: string; primaryKeyword?: string | null; topicCluster?: string | null }>): KeywordInventoryRow[] {
  const active = items.filter((x) => ["published", "scheduled", "review"].includes(x.status) && x.primaryKeyword);
  const counts = new Map<string, number>();
  for (const item of active) counts.set(item.primaryKeyword!.toLowerCase(), (counts.get(item.primaryKeyword!.toLowerCase()) ?? 0) + 1);
  const rows: KeywordInventoryRow[] = active.map((item) => ({ keyword: item.primaryKeyword!, ownerTitle: item.title, ownerPath: `/insights/${item.slug}`, status: item.status, cluster: item.topicCluster ?? undefined, overlap: (counts.get(item.primaryKeyword!.toLowerCase()) ?? 0) > 1, gap: false }));
  for (const cluster of approvedEditorialClusters) if (!items.some((x) => x.topicCluster === cluster && ["published", "scheduled", "review"].includes(x.status))) rows.push({ keyword: "", status: "gap", cluster, overlap: false, gap: true });
  return rows;
}

export const editorialTransition: Record<(typeof editorialStatus)[number], readonly string[]> = {
  draft: ["review", "published"],
  review: ["draft", "scheduled", "published"],
  scheduled: ["review", "published", "archived"],
  published: ["archived"],
  archived: [],
};
